/**
 * Support Page Component
 *
 * FAQ section and support ticket management system
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../contexts/AuthContext';
import { useUiMode } from '../contexts/UiModeContext';
import {
  getMyTickets,
  getTicketDetail,
  createTicket,
  addTicketMessage,
  type SupportTicket,
  type TicketMessage,
  type CreateTicketRequest,
} from '../services/api';
import {
  faqApi,
  type FAQCategory,
} from '../services/faqApi';

// FAQ item interface for grouped display
interface FAQItem {
  category: string;
  categorySlug: string;
  questions: Array<{
    id: string;
    slug: string;
    q: string;
    a: string;
  }>;
}

const TICKET_CATEGORIES = [
  { value: 'billing', label: 'Billing & Payments' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'account', label: 'Account & Access' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'other', label: 'Other' }
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low', color: 'bg-gray-100 text-gray-700' },
  { value: 'normal', label: 'Normal', color: 'bg-blue-100 text-blue-700' },
  { value: 'high', label: 'High', color: 'bg-orange-100 text-orange-700' },
  { value: 'urgent', label: 'Urgent', color: 'bg-red-100 text-red-700' }
];

const STATUS_COLORS: Record<string, string> = {
  open: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-yellow-100 text-yellow-700',
  waiting: 'bg-purple-100 text-purple-700',
  resolved: 'bg-green-100 text-green-700',
  closed: 'bg-gray-100 text-gray-700'
};

type TabType = 'faq' | 'tickets' | 'new';

export default function SupportPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('support');
  const { t: tc } = useTranslation('common');
  const { user, logout } = useAuth();
  const { effectiveMode } = useUiMode();
  const isEmployeeMode = effectiveMode === 'employee';
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('faq');

  // FAQ state - now fetched from API
  const [faqCategories, setFaqCategories] = useState<FAQCategory[]>([]);
  const [faqItems, setFaqItems] = useState<FAQItem[]>([]);
  const [isLoadingFaqs, setIsLoadingFaqs] = useState(true);
  const [faqError, setFaqError] = useState<string | null>(null);
  const [expandedFaq, setExpandedFaq] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Track loaded language to prevent re-fetching
  const [loadedLang, setLoadedLang] = useState<string | null>(null);

  // Get current language - memoize to prevent unnecessary recalculations
  const currentLang = i18n?.language?.split('-')[0] || 'en';

  // Tickets state
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [ticketMessages, setTicketMessages] = useState<TicketMessage[]>([]);
  const [isLoadingTickets, setIsLoadingTickets] = useState(false);
  const [newReply, setNewReply] = useState('');
  const [isSendingReply, setIsSendingReply] = useState(false);

  // New ticket state
  const [newTicket, setNewTicket] = useState<CreateTicketRequest>({
    subject: '',
    category: 'technical',
    priority: 'normal',
    description: ''
  });
  const [isCreatingTicket, setIsCreatingTicket] = useState(false);
  const [ticketError, setTicketError] = useState<string | null>(null);

  // Close user menu when clicking outside
  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Load FAQs from API
  const loadFaqs = useCallback(async (lang: string) => {
    setIsLoadingFaqs(true);
    setFaqError(null);
    try {
      // First get categories
      const categories = await faqApi.listCategories(lang);
      setFaqCategories(categories);

      // Then fetch FAQs for each category
      const faqsByCategory: FAQItem[] = [];
      for (const category of categories) {
        const faqs = await faqApi.listFAQs(category.slug, lang);
        if (faqs.length > 0) {
          faqsByCategory.push({
            category: category.name,
            categorySlug: category.slug,
            questions: faqs.map(faq => ({
              id: faq.id,
              slug: faq.slug,
              q: faq.question,
              a: faq.answer,
            })),
          });
        }
      }
      setFaqItems(faqsByCategory);
      setLoadedLang(lang);
    } catch (error) {
      console.error('Failed to load FAQs:', error);
      setFaqError(error instanceof Error ? error.message : 'Failed to load FAQs');
    } finally {
      setIsLoadingFaqs(false);
    }
  }, []);

  // Load FAQs on mount and when language changes
  useEffect(() => {
    // Only load if language changed or not yet loaded
    if (loadedLang !== currentLang) {
      loadFaqs(currentLang);
    }
  }, [currentLang, loadedLang, loadFaqs]);

  // Reload FAQs with current language (for retry/refresh)
  const reloadFaqs = useCallback(() => {
    setLoadedLang(null); // Force reload
    loadFaqs(currentLang);
  }, [currentLang, loadFaqs]);

  // Load tickets when tab changes
  useEffect(() => {
    if (activeTab === 'tickets') {
      loadTickets();
    }
  }, [activeTab]);

  const loadTickets = async () => {
    setIsLoadingTickets(true);
    try {
      const data = await getMyTickets();
      setTickets(data.tickets);
    } catch (error) {
      console.error('Failed to load tickets:', error);
    } finally {
      setIsLoadingTickets(false);
    }
  };

  const viewTicket = async (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    try {
      const data = await getTicketDetail(ticket.id);
      setTicketMessages(data.messages);
    } catch (error) {
      console.error('Failed to load ticket details:', error);
    }
  };

  const sendReply = async () => {
    if (!selectedTicket || !newReply.trim()) return;
    setIsSendingReply(true);
    try {
      await addTicketMessage(selectedTicket.id, newReply);
      const data = await getTicketDetail(selectedTicket.id);
      setTicketMessages(data.messages);
      setNewReply('');
    } catch (error) {
      console.error('Failed to send reply:', error);
    } finally {
      setIsSendingReply(false);
    }
  };

  const handleCreateTicket = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsCreatingTicket(true);
    setTicketError(null);

    if (!newTicket.subject.trim() || !newTicket.description.trim()) {
      setTicketError(tc('errors.validationError'));
      setIsCreatingTicket(false);
      return;
    }

    try {
      await createTicket(newTicket);
      setNewTicket({ subject: '', category: 'technical', priority: 'normal', description: '' });
      setActiveTab('tickets');
      loadTickets();
    } catch (error) {
      setTicketError(error instanceof Error ? error.message : 'Failed to create ticket');
    } finally {
      setIsCreatingTicket(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle search with API
  const handleSearch = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      // If search is too short, reload all FAQs
      loadFaqs(currentLang);
      return;
    }
    setIsLoadingFaqs(true);
    try {
      const results = await faqApi.searchFAQs(query, currentLang, 20);
      // Group results by category
      const grouped: Record<string, FAQItem> = {};
      for (const result of results) {
        if (!grouped[result.category_slug]) {
          const cat = faqCategories.find(c => c.slug === result.category_slug);
          grouped[result.category_slug] = {
            category: cat?.name || result.category_slug,
            categorySlug: result.category_slug,
            questions: [],
          };
        }
        grouped[result.category_slug].questions.push({
          id: result.id,
          slug: result.slug,
          q: result.question,
          a: result.answer,
        });
      }
      setFaqItems(Object.values(grouped));
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsLoadingFaqs(false);
    }
  }, [currentLang, faqCategories, loadFaqs]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch(searchQuery);
      } else if (loadedLang === currentLang) {
        // Already loaded, just reload to show all
        loadFaqs(currentLang);
      }
    }, 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Filter FAQ based on search (client-side fallback)
  const filteredFaq = faqItems;

  return (
    <div className="bg-gray-50" style={{ minHeight: 'calc(100vh - var(--health-banner-height, 0px))' }}>
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Top bar - hidden in employee mode (EmployeeHeader provides this) */}
          {!isEmployeeMode && (
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate('/voice')}
                  className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                  <span className="hidden sm:inline text-sm font-medium">{tc('buttons.back')}</span>
                </button>
                <div className="h-6 w-px bg-gray-300" />
                <div>
                  <h1 className="text-xl font-bold text-gray-900">{t('title')}</h1>
                  <p className="text-xs text-gray-500">{t('subtitle')}</p>
                </div>
              </div>

              {/* User Menu */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-sm font-semibold">
                    {user?.full_name?.charAt(0)?.toUpperCase() || 'U'}
                  </div>
                  <svg className={`w-4 h-4 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 py-2 z-50">
                    <button onClick={() => { setUserMenuOpen(false); navigate('/settings'); }} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">{tc('navigation.settings')}</button>
                    <button onClick={() => { setUserMenuOpen(false); navigate('/billing'); }} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50">{tc('navigation.billing')}</button>
                    {user?.role === 'admin' && <button onClick={() => { setUserMenuOpen(false); navigate('/admin'); }} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-purple-700 hover:bg-purple-50">{tc('navigation.adminPanel')}</button>}
                    <div className="border-t border-gray-100 my-1" />
                    <button onClick={handleLogout} className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50">{tc('navigation.logout')}</button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 -mb-px">
            <button
              onClick={() => setActiveTab('faq')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'faq'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('tabs.faq')}
            </button>
            <button
              onClick={() => setActiveTab('tickets')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'tickets'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('tabs.myTickets')}
              {tickets.filter(t => t.status !== 'closed' && t.status !== 'resolved').length > 0 && (
                <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-700 rounded-full">
                  {tickets.filter(t => t.status !== 'closed' && t.status !== 'resolved').length}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('new')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'new'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t('tabs.newTicket')}
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div className="space-y-6">
            {/* Search */}
            <div className="relative">
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t('faq.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {isLoadingFaqs && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2">
                  <div className="animate-spin h-5 w-5 border-2 border-blue-600 border-t-transparent rounded-full" />
                </div>
              )}
            </div>

            {/* FAQ Error */}
            {faqError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {faqError}
                <button
                  onClick={reloadFaqs}
                  className="ml-2 underline hover:no-underline"
                >
                  {tc('buttons.retry')}
                </button>
              </div>
            )}

            {/* FAQ Categories */}
            {isLoadingFaqs && faqItems.length === 0 ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full" />
              </div>
            ) : filteredFaq.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                {searchQuery ? t('faq.noResults', { query: searchQuery }) : t('faq.noFaqs')}
              </div>
            ) : (
              filteredFaq.map((category) => (
                <div key={category.categorySlug} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-900">{category.category}</h3>
                  </div>
                  <div className="divide-y divide-gray-200">
                    {category.questions.map((item) => {
                      const key = `${category.categorySlug}-${item.slug}`;
                      return (
                        <div key={key}>
                          <button
                            onClick={() => setExpandedFaq(expandedFaq === key ? null : key)}
                            className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                          >
                            <span className="font-medium text-gray-900 pr-4">{item.q}</span>
                            <svg
                              className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform ${expandedFaq === key ? 'rotate-180' : ''}`}
                              fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                          {expandedFaq === key && (
                            <div className="px-6 py-4 bg-blue-50 text-gray-700 text-sm leading-relaxed prose prose-sm max-w-none">
                              <ReactMarkdown>{item.a}</ReactMarkdown>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-6 text-white text-center">
              <h3 className="text-xl font-bold mb-2">{t('faq.cantFind')}</h3>
              <p className="text-blue-100 mb-4">{t('faq.supportTeamHelp')}</p>
              <button
                onClick={() => setActiveTab('new')}
                className="px-6 py-2 bg-white text-blue-600 rounded-lg font-medium hover:bg-blue-50 transition-colors"
              >
                {t('faq.submitTicket')}
              </button>
            </div>
          </div>
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Ticket List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="font-semibold text-gray-900">{t('tickets.yourTickets')}</h3>
                  <button
                    onClick={loadTickets}
                    className="text-gray-500 hover:text-gray-700"
                    title={t('tickets.refresh')}
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                  </button>
                </div>
                {isLoadingTickets ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto" />
                  </div>
                ) : tickets.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <svg className="w-12 h-12 mx-auto text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    {t('tickets.noTickets')}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
                    {tickets.map((ticket) => (
                      <button
                        key={ticket.id}
                        onClick={() => viewTicket(ticket)}
                        className={`w-full p-4 text-left hover:bg-gray-50 transition-colors ${
                          selectedTicket?.id === ticket.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 truncate">{ticket.subject}</p>
                            <p className="text-xs text-gray-500 mt-1">#{ticket.id} • {new Date(ticket.created_at).toLocaleDateString()}</p>
                          </div>
                          <span className={`px-2 py-0.5 text-xs rounded-full flex-shrink-0 ${STATUS_COLORS[ticket.status] || 'bg-gray-100 text-gray-700'}`}>
                            {ticket.status.replace('_', ' ')}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Ticket Detail */}
            <div className="lg:col-span-2">
              {selectedTicket ? (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  {/* Ticket Header */}
                  <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-900">{selectedTicket.subject}</h3>
                        <p className="text-sm text-gray-500 mt-1">
                          Ticket #{selectedTicket.id} • {t('tickets.createdOn')} {new Date(selectedTicket.created_at).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${
                          PRIORITY_OPTIONS.find(p => p.value === selectedTicket.priority)?.color || 'bg-gray-100 text-gray-700'
                        }`}>
                          {selectedTicket.priority}
                        </span>
                        <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[selectedTicket.status] || 'bg-gray-100 text-gray-700'}`}>
                          {selectedTicket.status.replace('_', ' ')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Original Description */}
                  <div className="p-6 border-b border-gray-200">
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedTicket.description}</p>
                  </div>

                  {/* Messages */}
                  <div className="p-6 space-y-4 max-h-64 overflow-y-auto">
                    {ticketMessages.length === 0 ? (
                      <p className="text-sm text-gray-500 text-center">{t('tickets.noReplies')}</p>
                    ) : (
                      ticketMessages.map((msg) => (
                        <div key={msg.id} className={`flex ${msg.is_admin ? 'justify-start' : 'justify-end'}`}>
                          <div className={`max-w-[80%] rounded-lg p-3 ${
                            msg.is_admin ? 'bg-gray-100 text-gray-800' : 'bg-blue-500 text-white'
                          }`}>
                            <p className="text-xs mb-1 opacity-70">{msg.is_admin ? t('tickets.supportTeam') : t('tickets.you')} • {new Date(msg.created_at).toLocaleString()}</p>
                            <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Reply Form */}
                  {selectedTicket.status !== 'closed' && (
                    <div className="p-4 border-t border-gray-200 bg-gray-50">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newReply}
                          onChange={(e) => setNewReply(e.target.value)}
                          placeholder={t('tickets.typeReply')}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendReply()}
                        />
                        <button
                          onClick={sendReply}
                          disabled={!newReply.trim() || isSendingReply}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                          {isSendingReply ? t('tickets.sending') : t('tickets.send')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center text-gray-500">
                  <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  <p>{t('tickets.selectTicket')}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* New Ticket Tab */}
        {activeTab === 'new' && (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">{t('newTicket.title')}</h3>
                <p className="text-sm text-gray-500 mt-1">{t('newTicket.responseTime')}</p>
              </div>
              <form onSubmit={handleCreateTicket} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('newTicket.subject')}</label>
                  <input
                    type="text"
                    value={newTicket.subject}
                    onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                    placeholder={t('newTicket.subjectPlaceholder')}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('newTicket.category')}</label>
                    <select
                      value={newTicket.category}
                      onChange={(e) => setNewTicket({ ...newTicket, category: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {TICKET_CATEGORIES.map((cat) => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('newTicket.priority')}</label>
                    <select
                      value={newTicket.priority}
                      onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {PRIORITY_OPTIONS.map((pri) => (
                        <option key={pri.value} value={pri.value}>{pri.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('newTicket.description')}</label>
                  <textarea
                    value={newTicket.description}
                    onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                    placeholder={t('newTicket.descriptionPlaceholder')}
                    rows={6}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    required
                  />
                </div>

                {ticketError && (
                  <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {ticketError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isCreatingTicket}
                  className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isCreatingTicket ? t('newTicket.submitting') : t('newTicket.submit')}
                </button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
